/**
 * Компонент сообщения в чате: входящие/исходящие с индикатором канала
 * Дата: 30.01.2026 - REDESIGN: Топовый современный дизайн bubble
 * Дата: 05.02.2026 - Добавлена поддержка медиа: фото, видео, документы (TODO-055)
 * Дата: 05.02.2026 - Добавлен прокси для Wazzup медиа (TODO-055)
 * 
 * СВЯЗАННЫЕ МОДУЛИ:
 * - ChatWindow.tsx - родительский компонент, передает message и channel
 * - MessageDTO из api/types.ts - структура сообщения (content_type, file_url)
 * - /api/media/proxy - бэкенд прокси для Wazzup медиа
 * 
 * ОСОБЕННОСТИ ДИЗАЙНА:
 * - Bubble с "хвостиком" как в Telegram/WhatsApp (CSS clip-path)
 * - Микроанимация появления (fade-in + slide)
 * - Мягкие тени по 8pt сетке
 * - Статусы с анимацией пульсации для "read"
 * - Превью изображений с lightbox (05.02.2026)
 * - Видео с inline плеером (05.02.2026)
 * - Документы с иконкой и кликабельной ссылкой (05.02.2026)
 */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Check, CheckCheck, Clock, AlertCircle, ArrowRight, RefreshCw, Image, FileText, Download, X, MapPin, Contact, StickyNote } from 'lucide-react';
import { cn } from '@/utils/cn';
import { MessageDTO } from '@/api/types';
import { formatLinePhone, smsLinePhone } from './smsLinePhone';

// 05.02.2026 - Преобразование Wazzup URL в прокси URL
// Wazzup contentUri требует авторизации, поэтому проксируем через бэкенд
function getProxiedUrl(url: string | undefined, tenantId?: string): string | undefined {
  if (!url) return undefined;
  
  // Если URL от Wazzup - проксируем
  if (url.includes('wazzup24.com') || url.includes('wazzup')) {
    const proxyUrl = `/api/media/proxy?url=${encodeURIComponent(url)}`;
    // Добавляем tenant_id если есть (для SuperAdmin)
    return tenantId ? `${proxyUrl}&tenant_id=${tenantId}` : proxyUrl;
  }
  
  // Остальные URL возвращаем как есть
  return url;
}

interface MessageBubbleProps {
  message: MessageDTO;
  showChannel?: boolean;
  channel?: string;
  tenantId?: string; // 05.02.2026 - для прокси медиа
  onShowFallbackChain?: (messageId: string) => void;
  // 25.08.2026 — НЕ УДАЛЯТЬ по требованию владельца: служебные пометки только superadmin, пациент не видит.
  isSuperAdmin?: boolean;
  hasTrainingNote?: boolean;
  onAddTrainingNote?: (messageId: string) => void;
}

// 30.01.2026 - Улучшенные badge с градиентами
const channelBadges: Record<string, { label: string; bg: string; text: string; glow?: string }> = {
  whatsapp: { label: 'WA', bg: 'bg-gradient-to-r from-green-400 to-green-500', text: 'text-white', glow: 'shadow-green-200' },
  whatsapp_waba: { label: 'WABA', bg: 'bg-gradient-to-r from-emerald-500 to-emerald-600', text: 'text-white', glow: 'shadow-emerald-200' },
  whatsapp_regular: { label: 'Reg', bg: 'bg-gradient-to-r from-green-400 to-green-500', text: 'text-white', glow: 'shadow-green-200' },
  waba: { label: 'WABA', bg: 'bg-gradient-to-r from-emerald-500 to-emerald-600', text: 'text-white', glow: 'shadow-emerald-200' },
  regular: { label: 'Reg', bg: 'bg-gradient-to-r from-green-400 to-green-500', text: 'text-white', glow: 'shadow-green-200' },
  telegram: { label: 'TG', bg: 'bg-gradient-to-r from-blue-400 to-blue-500', text: 'text-white', glow: 'shadow-blue-200' },
  telegram_bot: { label: 'TG Bot', bg: 'bg-gradient-to-r from-blue-400 to-blue-500', text: 'text-white', glow: 'shadow-blue-200' },
  telegram_client: { label: 'TG Cli', bg: 'bg-gradient-to-r from-sky-400 to-sky-500', text: 'text-white', glow: 'shadow-sky-200' },
  sms: { label: 'SMS', bg: 'bg-gradient-to-r from-amber-400 to-amber-500', text: 'text-white', glow: 'shadow-amber-200' },
  max: { label: 'MAX', bg: 'bg-gradient-to-r from-purple-400 to-purple-500', text: 'text-white', glow: 'shadow-purple-200' },
  max_client: { label: 'Max Cli', bg: 'bg-gradient-to-r from-fuchsia-400 to-fuchsia-500', text: 'text-white', glow: 'shadow-fuchsia-200' },
};

// 24.10.2026 — входящее слева белое, исходящее справа синее. Не удалять по требованию владельца системы.
const getBubbleStyles = (_channel: string | undefined, isOutgoing: boolean) => {
  if (!isOutgoing) {
    return {
      bg: 'border border-shell-border border-l-[3px] border-l-shell-muted bg-shell-surface',
      text: 'text-shell-text',
      timeText: 'text-shell-muted',
      metaText: 'text-shell-muted',
    };
  }

  return {
    bg: 'bg-shell-primary',
    text: 'text-white',
    timeText: 'text-white/75',
    metaText: 'text-white/80',
  };
};

// 30.01.2026 - Статусы доставки. Зелёный не используется как фон исходящего.
const statusConfig: Record<string, { icon: (animate?: boolean) => JSX.Element; title: string }> = {
  pending: { 
    icon: () => <Clock className="w-3.5 h-3.5 animate-pulse" />, 
    title: 'Ожидает отправки' 
  },
  sent: { 
    icon: () => <Check className="w-3.5 h-3.5" />, 
    title: 'Отправлено' 
  },
  delivered: { 
    icon: () => <CheckCheck className="w-3.5 h-3.5" />, 
    title: 'Доставлено' 
  },
  read: { 
    icon: () => <CheckCheck className="w-3.5 h-3.5" />, 
    title: 'Прочитано' 
  },
  failed: { 
    icon: () => <AlertCircle className="w-3.5 h-3.5 text-red-500 animate-pulse" />, 
    title: 'Ошибка доставки' 
  },
};

// 21.02.2026 - Lightbox через Portal: изолирован от ре-рендеров списка сообщений
function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
      style={{ isolation: 'isolate' }}
    >
      <button 
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
        onClick={onClose}
      >
        <X className="w-6 h-6 text-white" />
      </button>
      <img 
        src={src} 
        alt="Изображение" 
        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl select-none"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />
    </div>
  );
}

// 05.02.2026 - Компонент для отображения медиа контента
// 05.02.2026 - Добавлен tenantId для прокси Wazzup медиа
function MediaContent({ 
  contentType, 
  fileUrl, 
  content,
  tenantId
}: { 
  contentType: string; 
  fileUrl?: string; 
  content?: string;
  tenantId?: string;
}) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Получаем проксированный URL для Wazzup медиа
  const proxiedUrl = getProxiedUrl(fileUrl, tenantId);

  if (!proxiedUrl && !content?.startsWith('[')) return null;

  // Изображение
  if (contentType === 'image' && proxiedUrl) {
    return (
      <>
        <div 
          className="relative cursor-pointer group mb-2"
          onClick={() => setIsLightboxOpen(true)}
        >
          {imageError ? (
            <div className="w-48 h-32 bg-shell-elevated rounded-xl flex items-center justify-center">
              <Image className="w-8 h-8 text-shell-muted" />
            </div>
          ) : (
            <img 
              src={proxiedUrl} 
              alt="Изображение" 
              className="max-w-[280px] max-h-[280px] rounded-lg object-cover 
                         transition-all duration-200 group-hover:brightness-90"
              onError={() => setImageError(true)}
            />
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 
                          rounded-lg transition-colors duration-200" />
        </div>
        
        {/* Lightbox через Portal — рендерится вне дерева сообщений, не мерцает при ре-рендерах */}
        {isLightboxOpen && createPortal(
          <ImageLightbox src={proxiedUrl!} onClose={() => setIsLightboxOpen(false)} />,
          document.body
        )}
      </>
    );
  }

  // Видео
  if (contentType === 'video' && proxiedUrl) {
    return (
      <div className="relative mb-2">
        <video 
          src={proxiedUrl}
          controls
          className="max-w-[280px] max-h-[280px] rounded-lg"
          preload="metadata"
        >
          Ваш браузер не поддерживает видео
        </video>
      </div>
    );
  }

  // Аудио / голосовое сообщение
  if ((contentType === 'audio' || contentType === 'voice') && proxiedUrl) {
    return (
      <div className="mb-2">
        <audio 
          src={proxiedUrl}
          controls
          className="w-full max-w-[280px] h-10"
          preload="metadata"
        >
          Ваш браузер не поддерживает аудио
        </audio>
      </div>
    );
  }

  // Документ
  if (contentType === 'document' && proxiedUrl) {
    const fileName = fileUrl?.split('/').pop() || 'Документ';
    return (
      <a 
        href={proxiedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 mb-2 rounded-lg bg-white/50 dark:bg-white/5
                   hover:bg-white/80 dark:hover:bg-white/10 transition-colors group"
      >
        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center
                        group-hover:bg-blue-200 dark:group-hover:bg-blue-800/50 transition-colors">
          <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{fileName}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Документ</p>
        </div>
        <Download className="w-4 h-4 text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
      </a>
    );
  }

  // Геолокация
  if (contentType === 'location' || content?.includes('[Геолокация]')) {
    return (
      <div className="flex items-center gap-2 p-3 mb-2 rounded-lg bg-white/50 dark:bg-white/5">
        <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Геолокация</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Отправлено местоположение</p>
        </div>
      </div>
    );
  }

  // Контакт
  if (contentType === 'contact' || content?.includes('[Контакт]')) {
    return (
      <div className="flex items-center gap-2 p-3 mb-2 rounded-lg bg-white/50 dark:bg-white/5">
        <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
          <Contact className="w-5 h-5 text-green-600 dark:text-green-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Контакт</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Отправлен контакт</p>
        </div>
      </div>
    );
  }

  return null;
}

export function MessageBubble({ message, showChannel = false, channel, tenantId, onShowFallbackChain, isSuperAdmin, hasTrainingNote, onAddTrainingNote }: MessageBubbleProps) {
  const isOutgoing = message.direction === 'outgoing';
  const isAutoReply = message.metadata?.source === 'auto_reply';
  // 16.03.26 - Для WABA предпочитаем сохранённый preview snapshot из metadata,
  // чтобы старый/plain content не ломал отображение текста шаблона в чате.
  const wabaPreviewText = typeof message.metadata?.waba_preview_text === 'string'
    ? message.metadata.waba_preview_text
    : '';
  const displayContent = (
    isOutgoing &&
    (message.messenger_type === 'waba' || message.messenger === 'whatsapp_waba' || message.messenger === 'wazzup_waba') &&
    wabaPreviewText.trim() !== ''
  )
    ? wabaPreviewText
    : message.content;
  
  const getBadge = () => {
    if (message.messenger_type) {
      const typeKey = message.messenger_type as keyof typeof channelBadges;
      if (channelBadges[typeKey]) return channelBadges[typeKey];
    }
    
    const messengerKey = message.messenger as keyof typeof channelBadges;
    if (channelBadges[messengerKey]) return channelBadges[messengerKey];
    
    if (message.messenger?.includes('telegram')) return channelBadges.telegram;
    if (message.messenger?.includes('max')) return channelBadges.max;
    if (message.messenger === 'unknown' || !message.messenger) {
      return { label: '?', bg: 'bg-shell-elevated', text: 'text-shell-muted' };
    }
    
    return channelBadges.whatsapp;
  };
  
  const badge = getBadge();
  const styles = getBubbleStyles(channel || message.messenger, isOutgoing);
  const linePhone = smsLinePhone(message);
  
  return (
    <div 
      className={cn(
        'flex animate-in fade-in-0 slide-in-from-bottom-2 duration-200',
        isOutgoing ? 'justify-end' : 'justify-start'
      )}
    >
      {/* Bubble Container with Tail */}
      <div className="relative max-w-[min(640px,72%)] group">
        <div className={cn(
          'relative rounded-xl px-3.5 py-2.5',
          styles.bg,
          styles.text,
        )}>
          <div className="mb-1 flex items-center gap-2">
            <span className={cn('text-[11px] font-semibold uppercase tracking-wide', styles.metaText)}>
              {isOutgoing ? 'Исходящее' : 'Входящее'}
            </span>
            {showChannel && (
              <span className={cn('text-[11px] font-medium', styles.metaText)}>
                {badge.label}
              </span>
            )}
            {linePhone && (
              <span className="rounded-md bg-black/10 px-2 py-0.5 text-[12px] font-semibold tracking-normal">
                {isOutgoing ? 'С номера' : 'На номер'} {formatLinePhone(linePhone)}
              </span>
            )}
            {isOutgoing && isAutoReply && (
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-white">
                Автоответ
              </span>
            )}
            {!isOutgoing && (message.sender_name || message.patient_name) && (
              <span className="text-xs font-semibold text-shell-text">
                {message.sender_name || message.patient_name}
              </span>
            )}
          </div>
          
          {/* Media Content - 05.02.2026 */}
          <MediaContent 
            contentType={message.content_type}
            fileUrl={message.file_url}
            content={displayContent}
            tenantId={tenantId}
          />
          
          {/* Text Content - скрываем placeholder контент типа [Фото] */}
          {displayContent && !displayContent.startsWith('[') && (
          <p className="whitespace-pre-wrap break-words text-[14px] leading-5 [overflow-wrap:anywhere]">
            {displayContent}
          </p>
          )}
          
          {/* Time + Status - внутри bubble справа снизу */}
          <div className={cn(
            'flex items-center gap-1.5 mt-1.5',
            isOutgoing ? 'justify-end' : 'justify-start'
          )}>
            <span className={cn('text-[11px] font-medium', styles.timeText)}>
              {message.sent_at && formatDistanceToNow(new Date(message.sent_at), { 
                addSuffix: true, 
                locale: ru 
              })}
            </span>
            {isOutgoing && statusConfig[message.status] && (
              <span 
                title={statusConfig[message.status].title}
                className={cn(
                  'text-white/80 transition-transform duration-150 group-hover:scale-110',
                  message.status === 'failed' && 'text-shell-danger-soft',
                )}
              >
                {statusConfig[message.status].icon()}
              </span>
            )}
          </div>
          
          {/* Fallback индикатор */}
          {message.is_fallback && (
            <div className="flex items-center gap-1.5 mt-2 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800">
              <RefreshCw className="w-3 h-3 text-amber-600 dark:text-amber-400 animate-spin" style={{ animationDuration: '3s' }} />
              <span className="text-[11px] font-medium text-amber-700 dark:text-amber-300">Повторная отправка</span>
            </div>
          )}
          
          {/* Индикатор отправленного fallback */}
          {message.fallback_sent && message.fallback_channel && (
            <button
              onClick={() => onShowFallbackChain?.(message.id)}
              className="flex items-center gap-1.5 mt-2 px-2 py-1 rounded-lg bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 
                         hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors cursor-pointer group/fallback"
              title="Показать цепочку доставки"
            >
              <ArrowRight className="w-3 h-3 text-blue-600 group-hover/fallback:translate-x-0.5 transition-transform" />
              <span className="text-[11px] font-medium text-blue-700 dark:text-blue-300">→ {message.fallback_channel}</span>
            </button>
          )}
          
          {/* Причина ошибки */}
          {message.status === 'failed' && message.fail_reason && (
            <div className="mt-2 px-2 py-1 rounded-lg bg-red-50 dark:bg-red-950/60 border border-red-200 dark:border-red-800" title={message.fail_reason}>
              <span className="text-[11px] font-medium text-red-600 dark:text-red-400">
                ⚠️ {message.fail_reason.length > 40 ? message.fail_reason.slice(0, 40) + '...' : message.fail_reason}
              </span>
            </div>
          )}
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => onAddTrainingNote?.(message.id)}
              className={cn(
                'mt-2 inline-flex items-center gap-1 text-[11px] font-medium rounded-md px-1.5 py-0.5',
                hasTrainingNote
                  ? 'bg-amber-100 text-amber-800'
                  : isOutgoing
                    ? 'bg-white/15 text-white/80 hover:bg-white/25'
                    : 'bg-shell-elevated text-shell-muted hover:bg-shell-warning-soft hover:text-shell-warning'
              )}
              title="Служебная пометка"
              data-testid={`training-note-icon-${message.id}`}
            >
              <StickyNote className="w-3 h-3" />
              {hasTrainingNote ? 'пометка' : 'пометить'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

